<template>
  <v-row class="fill-height">
    <v-col cols="5" class="py-0">
      <not-received-campaign-card
        :value="notReceivedCampaigns"
        :loading="loading"
        card-type="primary"
        label="Clientes que não receberam disparos no período, dentro das campanhas concluídas"
        :quick-actions-props="notReceivedCampaignsQuickActionsProps"
      />
    </v-col>
    <v-col cols="7" class="py-0">
      <v-row class="fill-height">
        <v-col cols="6" class="py-0">
          <not-received-campaign-card
            :value="notReceivedCampaignsAndLost"
            :loading="loading"
            card-type="secondary"
            label="Clientes que não receberam campanhas e se tornaram perdidos nesse período"
            :quick-actions-props="notReceivedCampaignsAndLostQuickActionsProps"
          />
        </v-col>
        <v-col cols="6" class="py-0">
          <not-received-campaign-card
            :value="comeBackToBuy"
            :loading="loading"
            card-type="secondary"
            label="Clientes que voltaram a comprar depois de receber uma campanha"
            :quick-actions-props="receivedCampaignsAndCameBackToBuyQuickActionsProps"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { localeString, assertArray } from '@/config/helpers.js'
import {
  criteriaGroup,
  didNotReceiveMessageOnChannelCriteria,
  becameLostInPeriodCriteria,
  becameLostBeforePeriodCriteria,
  campaignReceivedCriteria,
  clubMembersPurchaseCriteria,
} from '@/components/quick-actions/segmentationGroups.js'
import { mapState } from 'vuex'
import { QUERY_TOTAL_CLIENTS_NOT_RECEIVED_CAMPAIGNS } from '@/modules/campanhas/resultados-gerais/graphql'

export default {
  name: 'NotReceivedCampaignsCards',
  components: {
    NotReceivedCampaignCard: () => import('@/modules/campanhas/resultados-gerais/pages/views/components/NotReceivedCampaignCard.vue'),
  },
  props: {
    campaignIds: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    locale: localeString,
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    defaultQueryVariables() {
      const { data } = this.stateFilters
      const [initialDate = '', finalDate = ''] = assertArray(data)
      const variables = {
        initialDate,
        finalDate,
        campaignIds: this.campaignIds,
      }

      return variables
    },
    skipQuery() {
      return !this.defaultQueryVariables || !this.defaultQueryVariables.initialDate || !this.defaultQueryVariables.finalDate || !this.campaignIds.length
    },
    loading() {
      return this.$apollo.queries.totalClientsNotReceivedCampaigns.loading
    },
    notReceivedCampaigns() {
      if (this.loading || !this.totalClientsNotReceivedCampaigns || !this.totalClientsNotReceivedCampaigns.not_received || !this.campaignIds.length) return '0'
      return this.locale(this.totalClientsNotReceivedCampaigns.not_received)
    },
    notReceivedCampaignsAndLost() {
      if (this.loading || !this.totalClientsNotReceivedCampaigns || !this.totalClientsNotReceivedCampaigns.not_received_and_lost || !this.campaignIds.length)
        return '0'
      return this.locale(this.totalClientsNotReceivedCampaigns.not_received_and_lost)
    },
    comeBackToBuy() {
      if (this.loading || !this.totalClientsNotReceivedCampaigns || !this.totalClientsNotReceivedCampaigns.come_back_to_buy || !this.campaignIds.length)
        return '0'
      return this.locale(this.totalClientsNotReceivedCampaigns.come_back_to_buy)
    },
    notReceivedCampaignsQuickActionsProps() {
      if (this.loading || this.skipQuery) return null
      return {
        iconColor: 'white',
        iconLarge: true,
        quickActionGroupId: `clients-did-not-receive-message-on-any-channel`,
        shortDescription: `Clientes que não receberam campanhas nesse período`,
        longDescription: ({ startDate, endDate }) => {
          return `Clientes que não receberam campanhas entre ${startDate} e ${endDate}`
        },
        segmentationGroup: ({ dateCriteriaFilter }) =>
          criteriaGroup([
            didNotReceiveMessageOnChannelCriteria(this.defaultQueryVariables.campaignIds, ['SMS', 'EMAIL', 'WHATSAPP', 'PUSH'], ...dateCriteriaFilter),
          ]),
      }
    },
    notReceivedCampaignsAndLostQuickActionsProps() {
      if (this.loading || this.skipQuery) return null
      return {
        iconColor: 'marca-1',
        iconLarge: true,
        quickActionGroupId: `clients-did-not-receive-message-on-any-channel-and-were-lost`,
        shortDescription: `Clientes que não receberam campanhas e se tornaram perdidos nesse período`,
        longDescription: ({ startDate, endDate }) => {
          return `Clientes que não receberam campanhas e se tornaram perdidos entre ${startDate} e ${endDate}`
        },
        segmentationGroup: ({ dateCriteriaFilter }) =>
          criteriaGroup([
            didNotReceiveMessageOnChannelCriteria(this.defaultQueryVariables.campaignIds, ['SMS', 'EMAIL', 'WHATSAPP', 'PUSH'], ...dateCriteriaFilter),
            becameLostInPeriodCriteria(...dateCriteriaFilter),
          ]),
      }
    },
    receivedCampaignsAndCameBackToBuyQuickActionsProps() {
      if (this.loading || this.skipQuery) return null
      return {
        iconColor: 'marca-1',
        iconLarge: true,
        quickActionGroupId: `clients-receive-message-on-any-channel-and-came-back`,
        shortDescription: `Clientes que voltaram a comprar e receberam uma campanha nesse período`,
        longDescription: ({ startDate, endDate }) => {
          return `Clientes que voltaram a comprar e receberam uma campanha entre ${startDate} e ${endDate}`
        },
        segmentationGroup: ({ dateCriteriaFilter }) =>
          criteriaGroup([
            campaignReceivedCriteria(this.defaultQueryVariables.campaignIds),
            becameLostBeforePeriodCriteria(...dateCriteriaFilter),
            clubMembersPurchaseCriteria(...dateCriteriaFilter),
          ]),
      }
    },
  },
  apollo: {
    totalClientsNotReceivedCampaigns: {
      query: QUERY_TOTAL_CLIENTS_NOT_RECEIVED_CAMPAIGNS,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      update({ totalClientsNotReceivedCampaigns }) {
        return totalClientsNotReceivedCampaigns
      },
      variables() {
        return this.defaultQueryVariables
      },
      context() {
        return {
          uri: this.$microservicesUrls['campaigns'],
        }
      },
      skip() {
        return this.skipQuery
      },
    },
  },
}
</script>
